import { EmailSettingsTabsEnum } from 'pages/email/constants';
import { CourseSettingsTabsEnum } from 'pages/course-settings/constants';
import { LoyaltyManagerTabsEnum } from 'pages/loyalty-manager/constants';
import { PresetsManagerTabsEnum } from 'pages/presets-manager/constants';
import { PriceManagementTabsEnum } from 'pages/pricing-management/constants';

import type { Key } from 'react';
import { SmsSettingsTabsEnum } from '../pages/sms/constants';

export type ValueOf<T> = T[keyof T];
export type RoutePathsType = ValueOf<typeof RoutePaths>;
export type PathCreatorValue = {
  path: string;
  getUrl: (args?: any) => string;
};

export type PathCreatorType = Record<RoutePathsType, PathCreatorValue>;

export enum RoutePaths {
  AUTH_LOGIN = 'auth_login',
  AUTH_LOGOUT = 'auth_logout',
  AUTH_CHANGE_PASSWORD = 'auth_change_password',
  AUTH_RECOVERY_PASSWORD = 'auth_recovery_password',
  AUTH_PROFILE = 'auth_profile',
  DASHBOARD = 'dashboard',
  DASHBOARD_PROPERTY = 'dashboard_property',
  API_STATUS = 'api_status',
  USERS_MANAGEMENT = 'users_management',
  USERS_MANAGEMENT_CREATION = 'users_management_creation',
  USERS_MANAGEMENT_UPDATE = 'users_management_update',
  COURSE_MANAGEMENT = 'course_management',
  COURSE_MANAGEMENT_CREATION = 'course_management_creation',
  COURSE_MANAGEMENT_UPDATE = 'course_management_update',
  PRICING_MANAGEMENT = 'pricing_management',
  PRICING_MANAGEMENT_RATES_CREATION = 'pricing_management_rates_creation',
  PRICING_MANAGEMENT_RATES_UPDATE = 'pricing_management_rates_update',
  PRICING_MANAGEMENT_RATES_COPY = 'pricing_management_rates_copy',
  PRICING_MANAGEMENT_PRICE_OVERRIDE_CREATION = 'pricing_management_price_override_creation',
  PRICING_MANAGEMENT_PRICE_OVERRIDE_UPDATE = 'pricing_management_price_override_update',
  RESERVATIONS_LIST = 'reservations_list',
  PRESETS_MANAGER = 'presets_manager',
  PRESETS_MANAGER_YIELD = 'presets_manager_yield',
  PRESETS_MANAGER_WEATHER = 'presets_manager_weather',
  TRADE_MANAGER = 'trade_manager',
  COURSE_SETTINGS = 'course_settings',
  TRADE_MANAGER_SETTINGS = 'trade_manager_settings',
  MEMBER_BOOKING_HISTORY = 'course_management_booking_history',
  TEE_TIME_MANAGER = 'tee_time_manager',
  LOYALTY_MANAGER = 'loyalty_manager',
  LOYALTY_MANAGER_PROMO_CREATION = 'loyalty_manager_create_promo',
  LOYALTY_MANAGER_PROMO_UPDATE = 'loyalty_manager_update_promo',
  STATISTICS = 'statistics',
  EMAIL_SETTINGS = 'email',
  SMS_SETTINGS = 'sms',
  SMS_MARKETING = 'sms-marketing',
  SMS_REMINDER = 'sms-reminder',
  SURVEY_REPORT = 'survey_report',
  SURVEY_SETTINGS = 'survey_settings',
  MARKETING = 'marketing',
  REMINDER = 'reminder',
  MARKETING_CAMPAIGN_CREATION = 'marketing_campaign_creation',
  MARKETING_CAMPAIGN_UPDATE = 'marketing_campaign_update',
  SMS_MARKETING_CAMPAIGN_CREATION = 'sms_marketing_campaign_creation',
  SMS_MARKETING_CAMPAIGN_UPDATE = 'sms_marketing_campaign_update',
}

export const PathCreator: PathCreatorType = {
  [RoutePaths.AUTH_LOGIN]: {
    path: '/login',
    getUrl: () => '/login',
  },
  [RoutePaths.AUTH_PROFILE]: {
    path: '/profile',
    getUrl: () => '/profile',
  },
  [RoutePaths.AUTH_LOGOUT]: {
    path: '/logout',
    getUrl: () => '/logout',
  },
  [RoutePaths.AUTH_CHANGE_PASSWORD]: {
    path: '/update-password',
    getUrl: () => '/update-password',
  },
  [RoutePaths.AUTH_RECOVERY_PASSWORD]: {
    path: '/forgot-password',
    getUrl: () => '/forgot-password',
  },
  [RoutePaths.DASHBOARD]: {
    path: '/',
    getUrl: () => '/',
  },
  [RoutePaths.DASHBOARD_PROPERTY]: {
    path: '/#',
    getUrl: () => '/#',
  },
  [RoutePaths.API_STATUS]: {
    path: '/api-status',
    getUrl: () => '/api-status',
  },
  [RoutePaths.USERS_MANAGEMENT]: {
    path: '/users',
    getUrl: () => '/users',
  },
  [RoutePaths.USERS_MANAGEMENT_CREATION]: {
    path: '/users/creation',
    getUrl: () => '/users/creation',
  },
  [RoutePaths.USERS_MANAGEMENT_UPDATE]: {
    path: '/users/update/:id',
    getUrl: (id: Key) => `/users/update/${id}`,
  },
  [RoutePaths.COURSE_MANAGEMENT]: {
    path: '/courses',
    getUrl: () => '/courses',
  },
  [RoutePaths.COURSE_MANAGEMENT_CREATION]: {
    path: '/course/create',
    getUrl: () => '/course/create',
  },
  [RoutePaths.COURSE_MANAGEMENT_UPDATE]: {
    path: '/course/update/:id',
    getUrl: () => '/course/update',
  },
  [RoutePaths.PRICING_MANAGEMENT]: {
    path: '/pricing/:tab(rates|yields|weather|price-overrides)+',
    getUrl: (tabName = PriceManagementTabsEnum.RATE) => `/pricing/${tabName}`,
  },
  [RoutePaths.PRICING_MANAGEMENT_RATES_CREATION]: {
    path: '/pricing/:tab(rates)+/:mode(create|update|copy)+',
    getUrl: () => '/pricing/rates/create',
  },
  [RoutePaths.PRICING_MANAGEMENT_RATES_UPDATE]: {
    path: '/pricing/:tab(rates)+/:mode(create|update|copy)+/:id',
    getUrl: (id: string) => `/pricing/rates/update/${id}`,
  },
  [RoutePaths.PRICING_MANAGEMENT_RATES_COPY]: {
    path: '/pricing/:tab(rates)+/:mode(create|update|copy)+/:id',
    getUrl: (id: string) => `/pricing/rates/copy/${id}`,
  },
  [RoutePaths.PRICING_MANAGEMENT_PRICE_OVERRIDE_CREATION]: {
    path: '/pricing/:tab(price-overrides)+/:mode(create|update)+',
    getUrl: () => '/pricing/price-overrides/create',
  },
  [RoutePaths.PRICING_MANAGEMENT_PRICE_OVERRIDE_UPDATE]: {
    path: '/pricing/:tab(price-overrides)+/:mode(create|update)+/:id',
    getUrl: (id: string) => `/pricing/price-overrides/update/${id}`,
  },
  [RoutePaths.RESERVATIONS_LIST]: {
    path: '/reservations',
    getUrl: () => '/reservations',
  },
  [RoutePaths.PRESETS_MANAGER]: {
    path: '/presets-manager/:tab',
    getUrl: (tabName = PresetsManagerTabsEnum.YIELD_PRESETS) => `/presets-manager/${tabName}`,
  },
  [RoutePaths.PRESETS_MANAGER_YIELD]: {
    path: '/presets-manager/:tab',
    getUrl: () => `/presets-manager/${PresetsManagerTabsEnum.YIELD_PRESETS}`,
  },
  [RoutePaths.PRESETS_MANAGER_WEATHER]: {
    path: '/presets-manager/:tab',
    getUrl: () => `/presets-manager/${PresetsManagerTabsEnum.WEATHER_PRESETS}`,
  },
  [RoutePaths.LOYALTY_MANAGER]: {
    path: '/loyalty-manager/:tab',
    getUrl: (tab = LoyaltyManagerTabsEnum.PROMO_CODE_MANAGER) => `/loyalty-manager/${tab}`,
  },
  [RoutePaths.LOYALTY_MANAGER_PROMO_CREATION]: {
    path: `/loyalty-manager/${LoyaltyManagerTabsEnum.PROMO_CODE_MANAGER}/create`,
    getUrl: () => `/loyalty-manager/${LoyaltyManagerTabsEnum.PROMO_CODE_MANAGER}/create`,
  },
  [RoutePaths.LOYALTY_MANAGER_PROMO_UPDATE]: {
    path: `/loyalty-manager/${LoyaltyManagerTabsEnum.PROMO_CODE_MANAGER}/update/:id`,
    getUrl: (id: string) => `/loyalty-manager/${LoyaltyManagerTabsEnum.PROMO_CODE_MANAGER}/update/${id}`,
  },
  [RoutePaths.TRADE_MANAGER]: {
    path: '/trade-manager',
    getUrl: () => '/trade-manager',
  },
  [RoutePaths.TRADE_MANAGER_SETTINGS]: {
    path: '/trade-manager/settings',
    getUrl: () => '/trade-manager/settings',
  },
  [RoutePaths.COURSE_SETTINGS]: {
    path: '/course-settings/:tab',
    getUrl: (tabName = CourseSettingsTabsEnum.CALENDAR) => `/course-settings/${tabName}`,
  },
  [RoutePaths.MEMBER_BOOKING_HISTORY]: {
    path: '/course-settings/booking-history/:id',
    getUrl: (id: string) => `/course-management/booking-history/${id}`,
  },
  [RoutePaths.TEE_TIME_MANAGER]: {
    path: '/tee-time-manager',
    getUrl: () => '/tee-time-manager',
  },
  [RoutePaths.STATISTICS]: {
    path: '/statistics',
    getUrl: () => '/statistics',
  },
  [RoutePaths.EMAIL_SETTINGS]: {
    path: '/email/:tab',
    getUrl: (tabName = EmailSettingsTabsEnum.REMINDER) => `/email/${tabName}`,
  },
  [RoutePaths.SURVEY_REPORT]: {
    path: '/email/:tab',
    getUrl: () => `/email/${EmailSettingsTabsEnum.SURVEY_REPORT}`,
  },
  [RoutePaths.SURVEY_SETTINGS]: {
    path: '/email/:tab',
    getUrl: () => `/email/${EmailSettingsTabsEnum.SURVEY_SETTINGS}`,
  },
  [RoutePaths.MARKETING]: {
    path: '/email/:tab',
    getUrl: () => `/email/${EmailSettingsTabsEnum.MARKETING}`,
  },
  [RoutePaths.REMINDER]: {
    path: '/email/:tab',
    getUrl: () => `/email/${EmailSettingsTabsEnum.REMINDER}`,
  },
  [RoutePaths.MARKETING_CAMPAIGN_CREATION]: {
    path: '/email/marketing/:mode(create|update)+',
    getUrl: () => '/email/marketing/create',
  },
  [RoutePaths.MARKETING_CAMPAIGN_UPDATE]: {
    path: '/email/marketing/:mode(create|update)+/:id',
    getUrl: (id: string) => `/email/marketing/update/${id}`,
  },
  [RoutePaths.SMS_SETTINGS]: {
    path: '/sms/:tab',
    getUrl: (tabName = SmsSettingsTabsEnum.REMINDER) => `/sms/${tabName}`,
  },
  [RoutePaths.SMS_MARKETING]: {
    path: '/sms/:tab',
    getUrl: () => `/sms/${SmsSettingsTabsEnum.MARKETING}`,
  },
  [RoutePaths.SMS_REMINDER]: {
    path: '/sms/:tab',
    getUrl: () => `/sms/${SmsSettingsTabsEnum.REMINDER}`,
  },
  [RoutePaths.SMS_MARKETING_CAMPAIGN_CREATION]: {
    path: '/sms/marketing/:mode(create|update)+',
    getUrl: () => '/sms/marketing/create',
  },
  [RoutePaths.SMS_MARKETING_CAMPAIGN_UPDATE]: {
    path: '/sms/marketing/:mode(create|update)+/:id',
    getUrl: (id: string) => `/sms/marketing/update/${id}`,
  },
};

